import { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpen, MagnifyingGlass, PaperPlaneRight, Funnel,
  ArrowUp, ShieldCheck, Tag, Archive, Pencil, Trash,
  CaretDown, Eye, Lock, BookmarkSimple
} from '@phosphor-icons/react';

const CATEGORY_META = {
  api_best_practices: { label: 'API Best Practices', emoji: '01' },
  error_patterns: { label: 'Error Patterns', emoji: '02' },
  performance_benchmarks: { label: 'Performance Benchmarks', emoji: '03' },
  integration_templates: { label: 'Integration Templates', emoji: '04' },
  security_advisories: { label: 'Security Advisories', emoji: '05' },
  schema_standards: { label: 'Schema Standards', emoji: '06' },
};

const ACCESS_TIER_STYLES = {
  baseline: 'bg-kova-cyan/10 text-kova-cyan border-kova-cyan/20',
  full: 'bg-kova-purple/10 text-kova-purple border-kova-purple/20',
  custom: 'bg-kova-gold/10 text-kova-gold border-kova-gold/20',
  all: 'bg-kova-green/10 text-kova-green border-kova-green/20',
};

export default function CommonsPage() {
  const [tab, setTab] = useState('browse');
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [accessLevel, setAccessLevel] = useState('baseline');
  const [filterCategory, setFilterCategory] = useState('');
  const [search, setSearch] = useState('');
  const [showPublish, setShowPublish] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.set('category', filterCategory);
      if (search) params.set('search', search);

      const [itemsRes, statsRes] = await Promise.all([
        fetch(`${API}/commons?${params}`, { credentials: 'include' }),
        fetch(`${API}/commons/stats`, { credentials: 'include' }),
      ]);

      if (itemsRes.ok) {
        const data = await itemsRes.json();
        setItems(data.items || []);
        setTotal(data.total || 0);
        setAccessLevel(data.access_level || 'baseline');
      }
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) {
      console.error('Commons fetch error:', e);
    }
    setLoading(false);
  }, [filterCategory, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpvote = async (itemId) => {
    const res = await fetch(`${API}/commons/${itemId}/upvote`, { method: 'POST', credentials: 'include' });
    if (res.ok) fetchData();
  };

  const handleDelete = async (itemId) => {
    const res = await fetch(`${API}/commons/${itemId}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) fetchData();
  };

  return (
    <div className="space-y-6" data-testid="commons-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-kova-white tracking-tight">MERA Commons</h1>
          <p className="font-mono text-[10px] tracking-[2px] uppercase text-kova-slate mt-1">
            F-011 Curated Intelligence Pool
            <span className={`ml-3 inline-block border px-1.5 py-0.5 ${ACCESS_TIER_STYLES[accessLevel] || ACCESS_TIER_STYLES.baseline}`}>
              {accessLevel.toUpperCase()} ACCESS
            </span>
          </p>
        </div>
        {(accessLevel === 'full' || accessLevel === 'custom') && (
          <button
            data-testid="publish-btn"
            onClick={() => setShowPublish(true)}
            className="flex items-center gap-2 font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 transition-colors cursor-crosshair"
          >
            <PaperPlaneRight size={14} weight="bold" />
            Contribute
          </button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-kova-cyan/12">
          <div className="bg-kova-card p-4">
            <p className="font-heading text-xl font-bold text-kova-white">{stats.pool?.total_published ?? 0}</p>
            <p className="font-mono text-[9px] tracking-[1px] uppercase text-kova-slate mt-1">Total Published</p>
          </div>
          <div className="bg-kova-card p-4">
            <p className="font-heading text-xl font-bold text-kova-white">{stats.contributions?.total ?? 0}</p>
            <p className="font-mono text-[9px] tracking-[1px] uppercase text-kova-slate mt-1">Your Contributions</p>
          </div>
          <div className="bg-kova-card p-4">
            <p className="font-heading text-xl font-bold text-kova-white">{stats.contributions?.drafts ?? 0}</p>
            <p className="font-mono text-[9px] tracking-[1px] uppercase text-kova-slate mt-1">Drafts</p>
          </div>
          <div className="bg-kova-card p-4">
            <p className="font-heading text-xl font-bold text-kova-amber">{stats.contributions?.upvotes_received ?? 0}</p>
            <p className="font-mono text-[9px] tracking-[1px] uppercase text-kova-slate mt-1">Upvotes Received</p>
          </div>
        </div>
      )}

      {/* Category Grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-kova-cyan/12">
          {Object.entries(CATEGORY_META).map(([key, meta]) => {
            const count = stats.pool?.by_category?.[key] ?? 0;
            return (
              <button
                key={key}
                data-testid={`category-${key}`}
                onClick={() => setFilterCategory(filterCategory === key ? '' : key)}
                className={`bg-kova-card p-4 text-left transition-colors cursor-crosshair ${filterCategory === key ? 'bg-kova-card2' : 'hover:bg-kova-card2'}`}
              >
                <span className="font-mono text-[9px] text-kova-cyan">{meta.emoji}</span>
                <p className="font-mono text-[9px] tracking-[1px] uppercase text-kova-slate mt-1">{meta.label}</p>
                <p className="font-heading text-lg font-bold text-kova-white mt-1">{count}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-kova-slate" />
          <input
            data-testid="commons-search"
            type="text"
            placeholder="Search intelligence..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-kova-card border border-kova-cyan/12 text-kova-white font-mono text-[10px] tracking-wide pl-8 pr-3 py-1.5 focus:outline-none focus:border-kova-cyan placeholder:text-kova-slate"
          />
        </div>
        {(filterCategory || search) && (
          <button
            onClick={() => { setFilterCategory(''); setSearch(''); }}
            className="text-kova-slate hover:text-kova-silv font-mono text-[9px] tracking-wide"
          >
            Clear
          </button>
        )}
        <span className="font-mono text-[9px] text-kova-slate tracking-wide ml-auto">{total} item{total !== 1 ? 's' : ''}</span>
      </div>

      {/* Items List */}
      {loading ? (
        <div className="text-center py-12 text-kova-slate font-mono text-xs">Loading commons...</div>
      ) : (
        <div className="space-y-1">
          {items.length === 0 ? (
            <div className="text-center py-12 border border-kova-cyan/12 bg-kova-card">
              <BookOpen size={32} className="mx-auto mb-3 text-kova-slate" />
              <p className="text-kova-silv text-sm">
                {search || filterCategory ? 'No items match your filters' : 'The Commons pool is empty. Be the first to contribute!'}
              </p>
            </div>
          ) : (
            items.map((item) => (
              <CommonsItemCard
                key={item.item_id}
                item={item}
                onUpvote={handleUpvote}
                onDelete={handleDelete}
                accessLevel={accessLevel}
              />
            ))
          )}
        </div>
      )}

      {/* Publish Modal */}
      <AnimatePresence>
        {showPublish && (
          <PublishModal
            onClose={() => setShowPublish(false)}
            onSuccess={() => { setShowPublish(false); fetchData(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function CommonsItemCard({ item, onUpvote, onDelete, accessLevel }) {
  const catMeta = CATEGORY_META[item.category] || { label: item.category, emoji: '--' };
  const tierStyle = ACCESS_TIER_STYLES[item.access_tier] || ACCESS_TIER_STYLES.baseline;
  const isLocked = (accessLevel === 'baseline' && item.access_tier === 'full') ||
                   (accessLevel !== 'custom' && item.access_tier === 'custom');

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-kova-card border border-kova-cyan/12 p-4 hover:border-kova-cyan/25 transition-colors group ${isLocked ? 'opacity-60' : ''}`}
      data-testid={`commons-item-${item.item_id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`inline-block border px-1.5 py-0.5 font-mono text-[9px] tracking-wide ${tierStyle}`}>
              {item.access_tier?.toUpperCase()}
            </span>
            <span className="font-mono text-[9px] text-kova-slate tracking-wide">{catMeta.label}</span>
            {isLocked && <Lock size={12} className="text-kova-slate" />}
          </div>
          <h3 className="font-heading text-sm font-bold text-kova-white">{item.title}</h3>
          {item.description && <p className="text-kova-silv text-xs mt-1 line-clamp-2">{item.description}</p>}
          <div className="flex items-center gap-3 mt-2 text-kova-slate font-mono text-[9px] tracking-wide">
            <span>{item.contributor_name || 'Anonymous'}</span>
            {item.published_at && <span>{new Date(item.published_at).toLocaleDateString()}</span>}
            {item.tags?.length > 0 && (
              <span className="flex items-center gap-1">
                <Tag size={10} />
                {item.tags.slice(0, 3).join(', ')}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onUpvote(item.item_id)}
            className="flex items-center gap-1 p-1.5 text-kova-slate hover:text-kova-amber transition-colors"
            title="Upvote"
          >
            <ArrowUp size={14} />
            <span className="font-mono text-[10px]">{item.upvotes || 0}</span>
          </button>
          {item.contributor_id && (
            <button
              onClick={() => onDelete(item.item_id)}
              className="p-1.5 text-kova-slate hover:text-kova-red transition-colors opacity-0 group-hover:opacity-100"
              title="Delete"
            >
              <Trash size={14} />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function PublishModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'api_best_practices',
    access_tier: 'full',
    tags: '',
    content: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        content: { text: form.content },
      };
      const res = await fetch(`${API}/commons/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.detail || 'Failed to publish');
        setSubmitting(false);
        return;
      }
      onSuccess();
    } catch (e) {
      setError('Network error');
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-kova-card border border-kova-cyan/12 p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="publish-modal"
      >
        <h2 className="font-heading text-lg font-bold text-kova-white mb-4">Contribute to Commons</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Category</label>
            <select
              data-testid="publish-category"
              value={form.category}
              onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))}
              className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 focus:outline-none focus:border-kova-cyan"
            >
              {Object.entries(CATEGORY_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Title</label>
            <input
              data-testid="publish-title"
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="e.g. Rate limiting best practices for payment APIs"
              className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 placeholder:text-kova-slate focus:outline-none focus:border-kova-cyan"
            />
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Description</label>
            <textarea
              data-testid="publish-description"
              value={form.description}
              onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
              rows={2}
              className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 placeholder:text-kova-slate focus:outline-none focus:border-kova-cyan resize-none"
            />
          </div>
          <div>
            <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Content</label>
            <textarea
              data-testid="publish-content"
              value={form.content}
              onChange={(e) => setForm(p => ({ ...p, content: e.target.value }))}
              rows={4}
              placeholder="Your intelligence contribution..."
              className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 placeholder:text-kova-slate focus:outline-none focus:border-kova-cyan resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Access Tier</label>
              <select
                data-testid="publish-access-tier"
                value={form.access_tier}
                onChange={(e) => setForm(p => ({ ...p, access_tier: e.target.value }))}
                className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 focus:outline-none focus:border-kova-cyan"
              >
                <option value="baseline">Baseline (everyone)</option>
                <option value="full">Full (Growth+)</option>
                <option value="all">All Tiers</option>
              </select>
            </div>
            <div>
              <label className="font-mono text-[9px] tracking-[2px] uppercase text-kova-slate block mb-1">Tags (comma-separated)</label>
              <input
                data-testid="publish-tags"
                type="text"
                value={form.tags}
                onChange={(e) => setForm(p => ({ ...p, tags: e.target.value }))}
                placeholder="api, security, patterns"
                className="w-full bg-kova-bg border border-kova-cyan/12 text-kova-white font-mono text-xs px-3 py-2 placeholder:text-kova-slate focus:outline-none focus:border-kova-cyan"
              />
            </div>
          </div>

          {error && <p className="text-kova-red font-mono text-xs">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="font-mono text-[10px] tracking-[2px] uppercase text-kova-silv border border-kova-cyan/12 px-4 py-2 hover:border-kova-cyan/25 hover:text-kova-white transition-colors">
              Cancel
            </button>
            <button
              data-testid="publish-submit"
              type="submit"
              disabled={submitting}
              className="font-mono text-[10px] tracking-[2px] uppercase bg-kova-cyan text-kova-bg px-4 py-2 hover:bg-kova-cyan2 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Publishing...' : 'Publish'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
